import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import FlexPassApi from "../../../api/flexpassApi";
import { ReactComponent as StatisticIcon } from "../../../assets/Certificates/merge.svg";
import CircleAvatar from "../../../components/CircleAvatar/CircleAvatar";
import ModalWindow from "../../../components/ModalWindow/ModalWindow";
import StandartButton from "../../../components/StandartButton/StandartButton";
import { ISO639Lang } from "../../../constans/languagesList";
import {
  FlexPassSubscription,
  SubscriptionType,
} from "../../../types/FlexPassTypes";
import { TypeUserData } from "../../../types/TypeUsers";
import { formatDateAndTime } from "../../../utils/dateActions";
import { getTranslation } from "../../../utils/getTranslation";
import Notify from "../../../utils/toaster";
import { flexPassTypeStrings } from "../flexPassUtils";
import s from "./FlexPassInfo.module.css";
import FlexPassInviteStatistic from "./FlexPassInviteStatistic/FlexPassInviteStatistic";
import InviteSpecialists from "./InviteSpecialists/InviteSpecialists";

type Props = {
  data: FlexPassSubscription;
  setInfoModalIsOpen: Dispatch<SetStateAction<boolean>>;
  setFlexPasses: Dispatch<SetStateAction<FlexPassSubscription[]>>;
  setSelectedFlexPass: Dispatch<
    SetStateAction<FlexPassSubscription | undefined>
  >;
};

type ParticipantType = Pick<
  TypeUserData,
  "_id" | "avatar" | "avatarS3" | "name" | "surname" | "shortId" | "email"
>;

export const FlexPassInfo: React.FC<Props> = ({
  data,
  setInfoModalIsOpen,
  setFlexPasses,
  setSelectedFlexPass,
}) => {
  const {
    title,
    description,
    language,
    mainSpecialization,
    level,
    startDate,
    endDate,
    cost,
    baseCost,
    duration,
    type,
    sessionDiscount,
    talziCommissionPercent,
    amountOfSessions,
    firstSessionPayout,
    repeatSessionPayout,
    entryCodes,
  } = data;

  const token = localStorage.getItem("token");
  const [activeTab, setActiveTab] = useState<"clients" | "specialists">(
    "clients",
  );
  const [participants, setParticipants] = useState<{
    clients: ParticipantType[];
    specialists: (ParticipantType & { specialistId: string })[];
  }>({ clients: [], specialists: [] });
  const [inviteModal, setInviteModal] = useState(false);
  const [statisticModal, setStatisticModal] = useState(false);
  const [loaders, setLoaders] = useState({ removeSpecLoader: false });

  useEffect(() => {
    (async () => {
      if (!token) return;
      const response = await FlexPassApi.getFlexPassParticipants(
        token,
        data._id,
      );

      if (response.status && response.participants) {
        setParticipants({
          clients: response.participants!.clients,
          specialists: response.participants!
            .specialists as (ParticipantType & { specialistId: string })[],
        });
      }
    })();
  }, [data]);

  const removeSpecialist = async (specialistId: string) => {
    if (!token) return;
    setLoaders((prev) => ({ ...prev, removeSpecLoader: true }));
    const response = await FlexPassApi.kickFlexPassSpecialist(
      token,
      specialistId,
      data._id,
    );
    setLoaders((prev) => ({ ...prev, removeSpecLoader: false }));
    if (response.status) {
      setParticipants((prev) => ({
        ...prev,
        specialists: prev.specialists.filter(
          (el) => el.specialistId !== specialistId,
        ),
      }));
    }
    if (!response.status && response.message) {
      Notify(response.message);
    }
  };

  const renderUserRow = (user: ParticipantType, specialistId?: string) => (
    <tr key={user._id}>
      <td>
        <div style={{ width: "fit-content" }}>
          <CircleAvatar
            userId={user._id}
            avatarProps={user.avatarS3 || user.avatar}
          />
        </div>
      </td>
      <td>
        {getTranslation(user.name, language)}{" "}
        {getTranslation(user.surname, language)}
      </td>
      <td>{user.email}</td>
      <td>
        {specialistId && (
          <button
            className={s.removeBtn}
            onClick={() => removeSpecialist(specialistId)}
            disabled={loaders.removeSpecLoader}
          >
            Remove
          </button>
        )}
      </td>
    </tr>
  );

  return (
    <div className={s.container}>
      <div className={s.infoSection}>
        <div className={s.row}>
          <span className={s.label}>Title:</span>
          <span className={s.value}>{title[0].text}</span>
        </div>
        {description && (
          <div className={s.description}>
            <span className={s.label}>Description:</span>
            <p className={s.value} style={{ whiteSpace: "pre-wrap" }}>
              {description[0].text}
            </p>
          </div>
        )}
        <div className={s.row}>
          <span className={s.label}>Specialization:</span>
          <span className={s.value}>
            {getTranslation(mainSpecialization.labels, "en")}
          </span>
        </div>
        <div className={s.row}>
          <span className={s.label}>Language:</span>
          <span className={s.value}>{ISO639Lang[language]}</span>
        </div>
        <div className={s.row}>
          <span className={s.label}>Level:</span>
          <span className={s.value}>{getTranslation(level.title, "en")}</span>
        </div>
        <div className={s.row}>
          <span className={s.label}>Start / End:</span>
          <span className={s.value}>
            {formatDateAndTime(new Date(startDate), "en").formattedDate} /{" "}
            {formatDateAndTime(new Date(endDate), "en").formattedDate}
          </span>
        </div>
        <div className={s.row}>
          <span className={s.label}>Cost:</span>
          <span className={s.value}>${cost / 100}</span>
        </div>
        {baseCost && (
          <div className={s.row}>
            <span className={s.label}>Base Cost:</span>
            <span className={s.value}>${baseCost / 100}</span>
          </div>
        )}
        <div className={s.row}>
          <span className={s.label}>Duration:</span>
          <span className={s.value}>{duration} days</span>
        </div>
        <div className={s.row}>
          <span className={s.label}>Type:</span>
          <span className={s.value}>{flexPassTypeStrings[type]}</span>
        </div>

        {type === SubscriptionType.DISCOUNT && (
          <>
            <div className={s.row}>
              <span className={s.label}>Session Discount:</span>
              <span className={s.value}>{sessionDiscount}%</span>
            </div>
            <div className={s.row}>
              <span className={s.label}>Commission:</span>
              <span className={s.value}>{talziCommissionPercent}%</span>
            </div>
          </>
        )}

        {type === SubscriptionType.SESSION_VOLUME && (
          <>
            <div className={s.row}>
              <span className={s.label}>Sessions:</span>
              <span className={s.value}>{amountOfSessions}</span>
            </div>
            <div className={s.row}>
              <span className={s.label}>First Payout:</span>
              <span className={s.value}>
                ${firstSessionPayout ? firstSessionPayout / 100 : 0}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.label}>Repeat Payout:</span>
              <span className={s.value}>
                ${repeatSessionPayout ? repeatSessionPayout / 100 : 0}
              </span>
            </div>
          </>
        )}
      </div>
      <div className={s.spaceBetween}>
        <div className={s.tabs}>
          <button
            className={`${s.tab} ${activeTab === "clients" ? s.active : ""}`}
            onClick={() => setActiveTab("clients")}
          >
            Clients ({participants.clients.length})
          </button>
          <button
            className={`${s.tab} ${
              activeTab === "specialists" ? s.active : ""
            }`}
            onClick={() => setActiveTab("specialists")}
          >
            Specialists ({participants.specialists.length})
          </button>
        </div>
        <div className={s.row20gap}>
          <div
            role="button"
            className={s.showStatsBtn}
            onClick={() => setStatisticModal(true)}
          >
            <StatisticIcon className={s.statsIcon} /> Show invites
          </div>
          <StandartButton
            action={() => setInviteModal(true)}
            buttonTitle="Invite experts"
          />
        </div>
      </div>
      <table className={s.userTable}>
        <thead>
          <tr>
            <th>Avatar</th>
            <th>Name</th>
            <th>Email</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {activeTab === "clients"
            ? participants.clients.map((c) => renderUserRow(c))
            : participants.specialists.map((s) =>
                renderUserRow(s, s.specialistId),
              )}
        </tbody>
      </table>
      <ModalWindow
        isOpen={inviteModal}
        setIsOpen={setInviteModal}
        width="fit-content"
      >
        <InviteSpecialists
          flexPassInfo={data}
          setModalVisible={setInviteModal}
          setFlexPasses={setFlexPasses}
          setSelectedFlexPass={setSelectedFlexPass}
        />
      </ModalWindow>
      {entryCodes && (
        <ModalWindow
          isOpen={statisticModal}
          setIsOpen={setStatisticModal}
          width="fit-content"
        >
          <FlexPassInviteStatistic
            entryCodes={entryCodes}
            setModalIsOpen={setStatisticModal}
            setInfoModalIsOpen={setInfoModalIsOpen}
            flexPassInfo={data}
          />
        </ModalWindow>
      )}
    </div>
  );
};
